<script>
import { ladFnCapitalize, debounce } from '@/utils'
import LadSearchCustomLoadingMixin from '@/utils/mixin/search-custom-loading.js'

/** https://codesandbox.io/s/vueautosuggest-multiple-vue-autosuggests-545ee?file=/AutosuggestInstance.vue */
export default {
  name: 'LadSearchCommon',
  mixins: [LadSearchCustomLoadingMixin],
  props: {
    submit: { type: Number, default: 0 },
    name: { type: String, required: true, default: '' },
    search: { type: String, default: '' },
  },
  data() {
    return {
      base: 'search',
      advanced: false,
      queryDebounce: () => {},
      form: {
        query: null,
        selected: null,
        daterange: [],
        filters: {
          price: { min: null, max: null, type: 'price' },
          duration: [],
          age: [],
          month: [],
        },
      },
      isFocus: false,
      suggestions: [],
      minQuery: 4,
      loading: false,
      sections: [
        'experience',
        'package',
        'category',
        'city',
        'country',
        'continent',
      ],
    }
  },
  computed: {
    isValidQuery() {
      const query = (this.form.query || '').trim()

      return !!query && query.length >= this.minQuery
    },
  },
  watch: {
    search: {
      immediate: true,
      handler(newValue, oldValue) {
        const isFirstLoad = typeof oldValue === 'undefined'

        /** valor por default del search */
        if (isFirstLoad || (!!newValue && this.form.query !== newValue)) {
          this.form.query = newValue
        }
      },
    },
    'form.query'(newValue) {
      this.$emit('on-change', newValue)
    },
    isFocus(newValue) {
      newValue ? this.$emit('on-focus') : this.$emit('on-blur')
    },
    isValidQuery: {
      immediate: true,
      handler(value) {
        this.$emit('is-valid', value)
      },
    },
  },
  beforeMount() {
    /** haciendo delay de medio segundo para no enviar queries cada vez que se teclea */
    this.queryDebounce = debounce(this.querySearch, 500)
  },
  methods: {
    componentName(name) {
      return `LadSearch${ladFnCapitalize(name)}${
        this.advanced ? 'Advanced' : ''
      }`
    },
    querySearch() {
      const query = (this.form.query || '').trim()

      /** quitando el elemento `empty` para cuando se hacen nuevas busquedas */
      this.suggestions = this.suggestions.filter((t) => t.name !== 'empty')

      const promise = this.isValidQuery
        ? new Promise((resolve, reject) => {
            this.loading = true

            this.$axios
              .get(`/api/search/${query.removeDiacritical()}`)
              .then((response) => {
                const { status, data } = response

                if (status === 'success') {
                  const list = data.filter((t) => t.data.length > 0)
                  list.map((t) => (t.name = t.type))

                  /** levantando el listado de resultado para el btn search y el event Enter */
                  if (process.client) {
                    const item = this.$el.querySelector('#autosuggest__input')
                    item.dispatchEvent(new Event('click'))
                  }

                  // const total = data.reduce(
                  //   (current, item) => current + item.data.length,
                  //   0
                  // )

                  // if (total === 0) {
                  //   list = [
                  //     {
                  //       name: 'empty',
                  //       data: [{ name: this.form.query, uuid: 1 }],
                  //     },
                  //   ]
                  // }

                  resolve(list)
                } else {
                  // eslint-disable-next-line prefer-promise-reject-errors
                  reject()
                }
              })
              .catch((error) => reject(error))
              .finally(() => {
                this.loading = false
                this.sendAnalytics()
              })
          })
        : new Promise((resolve, reject) => resolve([]))

      return promise.then((response) => (this.suggestions = response))
    },
    resetQuery() {
      this.form.query = ''
      this.form.selected = null
      this.suggestions = []

      this.handleClick()
    },
    handleFocus(ev) {
      this.isFocus = true

      setTimeout(() => {
        const item = document.querySelector(
          '.home-wrapper--search-autocomplete .el-main'
        )
        if (item)
          this.$scrollTo('.home-wrapper--search-autocomplete .el-main', 400, {
            offset: -280,
          })
      }, 200)
    },
    handleBlur() {
      if (process.client) {
        const item = this.$el.querySelector('#autosuggest__input')

        item.blur()
        this.isFocus = false
      }
    },
    handleClick() {
      if (process.client) {
        const item = this.$el.querySelector('#autosuggest__input')

        item.focus()
      }
    },
    /** event fired when clicking on the input */
    onSelected(option) {
      if (process.client) {
        this.form.selected = option
        if (!option) return

        const element = document.querySelector(
          `[data-type="${option.name}-${option.item.uuid || option.item.id}"]`
        )
        if (!element) return

        if (this.advanced) {
          this.form.query = option.item.name
        }

        if (
          option.name !== 'empty' &&
          typeof element.pathname !== 'undefined' &&
          !!element.pathname
        ) {
          if (this.advanced) {
            this.form.query = ''
          }

          if (window.location.pathname !== element.pathname) {
            this.handleLoading({
              target: { localName: 'a', pathname: element.pathname },
            })
          }

          this.$router.push({ path: element.pathname })
        } else {
          element.dispatchEvent(new Event('click'))
        }
      }
    },
    /**
     * This is what the <input/> value is set to when you are selecting a suggestion.
     */
    getSuggestionValue(suggestion) {
      return this.form.query
    },
    sendAnalytics() {
      if (process.browser) {
        if (typeof this.$gtag !== 'undefined') {
          this.$gtag('event', 'search', this.form.query)
        }

        const experiences = this.suggestions.find(
          (t) => t.type === 'experience'
        )
        const items = !!experiences && experiences.data ? experiences.data : []

        if (typeof this.$fb !== 'undefined') {
          const params = {
            content_type: 'destination',
            content_ids: [] /** specific to the top 3-5 destinations listed in search results */,
            travel_start: null /** solo aplica cuando hay filtro de rango de fecha */,
            travel_end: null /** solo aplica cuando hay filtro de rango de fecha */,
            city: null /** solo aplica cuando se busca por ciudad */,
            region: null /** region or state => no aplica de momento */,
            country: null /** se obtiene del primer item */,
            num_adults: 1,
            num_children: null /** if applicable> */,
          }

          /** obtengo maximo 5 elementos listados */
          for (let i = 0; i < items.length && i < 5; i++) {
            params.content_ids.push(items[i].uuid)

            if (i === 0) {
              params.country = items[i].country.name
              params.city = items[i].starting_point
            }
          }

          Object.keys(params)
            .filter(
              (t) => !['content_type', 'content_ids', 'num_adults'].includes(t)
            )
            .filter((t) => !params[t])
            .map((t) => delete params[t])

          this.$fb.track('Search', params)
        }

        if (typeof this.$tiktok !== 'undefined') {
          const contents = items.map((item) => ({
            content_id: item.uuid,
            content_type: item.type,
            content_name: item.title,
            content_country: item.country.name,
            content_city: item.starting_point,
            content_brand: item.is_lad ? 'localadventures' : 'third party',
          }))

          this.$tiktok.track('Search', {
            contents,
            query: this.form.query,
          })
        }
      }
    },
  },
}
</script>

<style lang="scss">
.lad-autocomplete-search-wrapper {
  .before-input {
    i {
      margin: 0;
      padding: 0 10px;
      width: auto;
      border: 1px solid #dcdfe6;
      border-right: 0;
      cursor: pointer;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
  }

  .after-input {
    button {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  input,
  input:hover,
  input:focus {
    outline: none;
    border-color: #dcdfe6;
    border-radius: 0;
    border-right: 0;
  }

  #autosuggest {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;

    i {
      color: #ccc;
    }

    &__input {
      position: relative;
      width: auto;
      box-sizing: border-box;
      border: 1px solid #dcdfe6;
      border-left: 0;
      border-right: 0;
      color: #606266;

      &.autosuggest__input--open ~ .autosuggest__results-container {
        position: absolute;
        width: 100%;
        top: 52px;

        &::before {
          position: absolute;
          display: block;
          content: ' ';
          width: 0;
          height: 0;
          border: 6px solid transparent;
          top: -11px;
          left: 35px;
          border-bottom-color: #f7f7f7;
          z-index: 3005;
        }
      }
    }

    & > * {
      display: inline;
    }
  }

  .autosuggest__results {
    left: 0;
    margin: 20px 0 0 0;
    position: absolute;
    z-index: 3000;
    width: 100%;
    border: 1px solid #e0e0e0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    background: white;
    padding: 0;
    overflow: auto hidden;

    & > ul {
      list-style: none;
      margin: 0;
      padding: 0;

      & > li {
        color: #606266;

        &.item-search-header {
          font-size: 0.8em;
          text-transform: uppercase;
          text-align: right;
          line-height: 2;
          background-color: #f7f7f7;
          padding: 0 10px;
        }

        & + li {
          margin-top: 0;
          border-top: 1px solid #eaeaea;
        }

        a {
          display: block;
          padding: 5px 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 1.8;

          span {
            &.item-search-description {
              font-style: italic;
              color: #989898;
              font-size: 0.9em;
              line-height: 1.4;
            }
          }
        }
      }
    }
  }
}
</style>
